import type {LabelColorOptions} from '@primer/react'
import type {HydratedAPITask} from '../types/api-task'
import type {Session} from '../types/session'
import {ENVIRONMENTS_POLL_INTERVAL_MS} from '@github-ui/copilot-shared-ui/environments/use-environments'
import {resolveEnvironmentStatus} from '@github-ui/copilot-shared-ui/environments/environment-display'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {announce} from '@github-ui/aria-live'
import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {agentTaskQueryKey, fetchTask} from './use-task'
import {useEnvironmentFromApi} from './use-environment-from-api'
import {usePassiveRelayState} from './use-is-ahp-session'

export interface SidebarTaskEnvironmentStatus {
  label: string
  variant: LabelColorOptions
  refreshFailed: boolean
}

export interface SidebarEnvironmentStatusSubject {
  id?: string
  nwo?: string
  current_environment?: {
    id?: string
    kind: string
  } | null
  latest_session_id?: string
  latest_session_updated_at?: string
  row_version?: string
}

export function useAnnounceSidebarEnvironmentStatus(
  environmentStatus: SidebarTaskEnvironmentStatus | undefined,
  lifecycleActive = true,
  lifecycleKey?: string,
) {
  const previousAnnouncementRef = useRef<string | undefined>(undefined)
  const wasLifecycleActiveRef = useRef(false)
  const previousLifecycleKeyRef = useRef(lifecycleKey)
  const isLoading = environmentStatus?.label === 'Loading status'
  const announcement = isLoading
    ? undefined
    : environmentStatus
      ? `Cloud status: ${environmentStatus.label}.${environmentStatus.refreshFailed ? ' Update failed.' : ''}`
      : undefined

  useEffect(() => {
    if (!lifecycleActive) {
      wasLifecycleActiveRef.current = false
      previousAnnouncementRef.current = undefined
      previousLifecycleKeyRef.current = lifecycleKey
      return
    }

    if (!wasLifecycleActiveRef.current || previousLifecycleKeyRef.current !== lifecycleKey) {
      wasLifecycleActiveRef.current = true
      previousLifecycleKeyRef.current = lifecycleKey
      previousAnnouncementRef.current = announcement
      return
    }

    if (isLoading) return

    if (announcement && announcement !== previousAnnouncementRef.current) {
      announce(announcement)
    }
    previousAnnouncementRef.current = announcement
  }, [announcement, isLoading, lifecycleActive, lifecycleKey])
}

function sessionUpdatedAt(session: Session): string | undefined {
  return session.updated_at ?? session.last_updated_at
}

function getLatestMatchingSidebarSession(
  task: SidebarEnvironmentStatusSubject,
  hydratedTask: HydratedAPITask | undefined,
): Session | undefined {
  const taskId = task.id
  const environmentId = task.current_environment?.id
  if (!taskId || !environmentId || hydratedTask?.id !== taskId) return undefined
  if (hydratedTask.current_environment?.id !== environmentId) return undefined

  const matchingSessions = hydratedTask.sessions.filter(
    candidate =>
      (candidate.task_id === undefined || candidate.task_id === taskId) && candidate.environment_id === environmentId,
  )
  if (task.latest_session_id) {
    return matchingSessions.find(candidate => candidate.id === task.latest_session_id)
  }

  return matchingSessions.reduce<Session | undefined>(
    (latest, candidate) =>
      !latest || new Date(candidate.created_at).getTime() > new Date(latest.created_at).getTime() ? candidate : latest,
    undefined,
  )
}

export function getCurrentSidebarSession(
  task: SidebarEnvironmentStatusSubject,
  hydratedTask: HydratedAPITask | undefined,
): Session | undefined {
  const session = getLatestMatchingSidebarSession(task, hydratedTask)
  if (!session || (task.latest_session_id && task.latest_session_id !== session.id)) return undefined

  const detailUpdatedAt = sessionUpdatedAt(session)
  if (
    task.latest_session_updated_at &&
    detailUpdatedAt &&
    new Date(task.latest_session_updated_at).getTime() > new Date(detailUpdatedAt).getTime()
  ) {
    return undefined
  }
  return session
}

function hasStaleSidebarSessionIdentity(
  task: SidebarEnvironmentStatusSubject,
  hydratedTask: HydratedAPITask | undefined,
  validatedUnversionedRow: string | undefined,
): boolean {
  if (!hydratedTask) return false
  if (hydratedTask.id !== task.id || hydratedTask.current_environment?.id !== task.current_environment?.id) return true

  const session = getLatestMatchingSidebarSession(task, hydratedTask)
  if (!session) return !!task.latest_session_id
  if (task.latest_session_id && task.latest_session_id !== session.id) return true

  const detailUpdatedAt = sessionUpdatedAt(session)
  if (task.latest_session_updated_at && !detailUpdatedAt) {
    return validatedUnversionedRow !== task.latest_session_updated_at
  }
  return (
    !!task.latest_session_updated_at &&
    !!detailUpdatedAt &&
    new Date(task.latest_session_updated_at).getTime() > new Date(detailUpdatedAt).getTime()
  )
}

function hasUnvalidatedSidebarRowIdentity(
  task: SidebarEnvironmentStatusSubject,
  hydratedTask: HydratedAPITask | undefined,
  validatedRowVersion: string | undefined,
  rowVersion: string | undefined,
): boolean {
  if (!rowVersion || !hydratedTask) return false
  if (task.row_version) return validatedRowVersion !== rowVersion

  const session = getLatestMatchingSidebarSession(task, hydratedTask)
  return (
    !!task.latest_session_updated_at && !!session && !sessionUpdatedAt(session) && validatedRowVersion !== rowVersion
  )
}

export function isTaskResponseAlreadyValidated(
  hasCompletedRequest: boolean,
  dataUpdatedAt: number,
  apiURL: string | undefined,
  lastValidated: {dataUpdatedAt: number; apiURL: string | undefined} | undefined,
) {
  return !hasCompletedRequest && dataUpdatedAt === lastValidated?.dataUpdatedAt && apiURL === lastValidated?.apiURL
}

export function useSidebarTaskEnvironmentStatus(
  task: SidebarEnvironmentStatusSubject,
  options: {observe?: boolean; apiURL?: string} = {},
): SidebarTaskEnvironmentStatus | undefined {
  const taskId = task.id
  const environmentId = task.current_environment?.id
  const eligible =
    isFeatureEnabled('copilot_mission_control_environments_web_kickoff') &&
    !!taskId &&
    !!environmentId &&
    task.current_environment?.kind === 'managed-sandbox'
  const enabled = eligible && (options.observe ?? true)
  const [owner, repo] = task.nwo?.split('/', 2) ?? []
  const rowVersion = task.row_version ?? task.latest_session_updated_at
  const apiURL = options.apiURL
  const rowVersionRef = useRef(rowVersion)
  useLayoutEffect(() => {
    rowVersionRef.current = rowVersion
  }, [rowVersion])
  const completedTaskRequestRef = useRef<{rowVersion: string | undefined; apiURL: string | undefined} | undefined>(
    undefined,
  )
  // Row freshness validates a shared task response; it is not part of the task cache identity.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const taskQuery = useQuery<HydratedAPITask>({
    queryKey: agentTaskQueryKey(owner, repo, taskId, apiURL),
    queryFn: async ({signal}) => {
      if (!taskId) throw new Error('taskId is required')
      const requestedRowVersion = rowVersionRef.current
      const data = await fetchTask({taskId, signal, apiURL})
      completedTaskRequestRef.current = {rowVersion: requestedRowVersion, apiURL}
      return data
    },
    enabled,
    subscribed: enabled,
    refetchInterval: enabled ? ENVIRONMENTS_POLL_INTERVAL_MS : false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const refetchTask = taskQuery.refetch
  const previousRowVersionRef = useRef(rowVersion)
  const lastValidatedDataRef = useRef<{dataUpdatedAt: number; apiURL: string | undefined} | undefined>(undefined)
  const [validatedRow, setValidatedRow] = useState<
    {rowVersion: string | undefined; apiURL: string | undefined} | undefined
  >(undefined)
  const validatedRowVersion = validatedRow && validatedRow.apiURL === apiURL ? validatedRow.rowVersion : undefined
  useEffect(() => {
    if (!enabled || previousRowVersionRef.current === rowVersion) return
    previousRowVersionRef.current = rowVersion
    void refetchTask()
  }, [enabled, refetchTask, rowVersion])
  useEffect(() => {
    if (
      !enabled ||
      taskQuery.isFetching ||
      taskQuery.isError ||
      !taskQuery.data ||
      isTaskResponseAlreadyValidated(
        !!completedTaskRequestRef.current,
        taskQuery.dataUpdatedAt,
        apiURL,
        lastValidatedDataRef.current,
      )
    ) {
      return
    }
    if (!completedTaskRequestRef.current) {
      if (hasUnvalidatedSidebarRowIdentity(task, taskQuery.data, validatedRowVersion, rowVersion)) {
        void refetchTask()
      }
      return
    }
    const completedTaskRequest = completedTaskRequestRef.current
    completedTaskRequestRef.current = undefined
    if (completedTaskRequest.apiURL !== apiURL) {
      if (hasUnvalidatedSidebarRowIdentity(task, taskQuery.data, validatedRowVersion, rowVersion)) {
        void refetchTask()
      }
      return
    }
    if (completedTaskRequest.rowVersion !== rowVersion) {
      void refetchTask()
      return
    }
    lastValidatedDataRef.current = {dataUpdatedAt: taskQuery.dataUpdatedAt, apiURL}
    setValidatedRow(completedTaskRequest)
  }, [
    apiURL,
    enabled,
    refetchTask,
    rowVersion,
    task,
    taskQuery.data,
    taskQuery.dataUpdatedAt,
    taskQuery.isError,
    taskQuery.isFetching,
    validatedRowVersion,
  ])
  const environmentQuery = useEnvironmentFromApi(environmentId, apiURL, {
    enabled,
    subscribed: enabled,
    refetchInterval: enabled ? ENVIRONMENTS_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
  })
  const hasUnresolvedSessionIdentity = hasStaleSidebarSessionIdentity(task, taskQuery.data, validatedRowVersion)
  const hasUnvalidatedRowIdentity = hasUnvalidatedSidebarRowIdentity(
    task,
    taskQuery.data,
    validatedRowVersion,
    rowVersion,
  )
  const isSessionIdentityPending =
    (taskQuery.isFetching && hasUnresolvedSessionIdentity) || (!taskQuery.isError && hasUnvalidatedRowIdentity)
  const session =
    isSessionIdentityPending || (taskQuery.isError && (hasUnresolvedSessionIdentity || hasUnvalidatedRowIdentity))
      ? undefined
      : getCurrentSidebarSession(task, taskQuery.data)
  const relayEnabled = enabled && !apiURL
  const relay = usePassiveRelayState(
    relayEnabled ? taskId : undefined,
    relayEnabled ? environmentId : undefined,
    relayEnabled ? session?.id : undefined,
  )

  if (!eligible || !enabled) return undefined
  if (taskQuery.isLoading || isSessionIdentityPending || !environmentQuery.data) {
    return {
      label:
        taskQuery.isLoading || isSessionIdentityPending || environmentQuery.isLoading
          ? 'Loading status'
          : 'Status unavailable',
      variant: 'secondary',
      refreshFailed: false,
    }
  }

  return {
    ...resolveEnvironmentStatus(environmentQuery.data, relay.reachable, relay.connectionProgress, relay.relayStatus),
    refreshFailed: environmentQuery.isError || taskQuery.isError,
  }
}
